import React from "react";
import { Skeleton } from "@/components/ui/skeleton";

export type PercentageTextProps = {
  value: number | string | null | undefined;
  isLoading?: boolean;
  className?: string;
  maximumFractionDigits?: number;
  showPlusForPositive?: boolean;
  // What to display when value is null/undefined/empty or explicitly UNDEFINED/LOADING tokens
  emptyFallback?: string;
  // Treat any of these string values (case-insensitive) as N/A and render a dash
  naTokens?: string[];
  // Width of the skeleton placeholder
  skeletonWidthClass?: string;
};

/**
 * Reusable percentage renderer with consistent handling for loading, N/A and nullish values.
 */
const PercentageText: React.FC<PercentageTextProps> = ({
  value,
  isLoading = false,
  className = "text-[11px] text-brand-primary-new/60 font-normal",
  maximumFractionDigits = 1,
  showPlusForPositive = true,
  emptyFallback = "",
  naTokens = ["N/A"],
  skeletonWidthClass = "w-[48px]",
}) => {
  // Loading shimmer
  if (isLoading) {
    return (
      <div className={skeletonWidthClass}>
        <Skeleton className="h-[16px]" />
      </div>
    );
  }

  // Null/undefined or empty string
  if (value === null || value === undefined || value === "") {
    return <span className={className}>{emptyFallback}</span>;
  }

  // String handling for special tokens
  if (typeof value === "string") {
    const upper = value.toUpperCase();
    // Treat explicit N/A as dash
    if (naTokens.some((t) => upper === t.toUpperCase())) {
      return <span className={className}>-</span>;
    }
    // Treat tokens like UNDEFINED as empty
    if (upper === "UNDEFINED") {
      return <span className={className}>{emptyFallback}</span>;
    }
    // Any other string is returned as-is
    return <span className={className}>{value}</span>;
  }

  // Numeric formatting
  if (typeof value === "number" && !Number.isNaN(value)) {
    const sign = showPlusForPositive && value > 0 ? "+" : "";
    const formatted = value.toLocaleString(undefined, {
      maximumFractionDigits,
    });
    return <span className={className}>{`${sign}${formatted}%`}</span>;
  }

  return <span className={className}>{emptyFallback}</span>;
};

export default PercentageText;
